import DeleteModal from "components/DeleteModal/DeleteModal";
import { AuthContext } from "context/appContext";
import { Service } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { deleteService, getServiceByUserId, initState } from "redux/features/service/serviceSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Pagination from "shared/Pagination/Pagination";
import { Spinner } from "@material-tailwind/react";
import { itemPerDashborad, itemPerDashboradPrestation } from "data/constants";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Edit,
  Trash2,
  Plus,
  Package,
  Calendar,
  MapPin,
  Search,
  Filter,
  Grid,
  List as ListIcon,
} from "lucide-react";

export interface SectionGridFilterCardServiceAgentProps {
  className?: string;
}

const SectionGridFilterCardServiceAgent: FC<SectionGridFilterCardServiceAgentProps> = ({ className = "" }) => {
  const authContext = useContext(AuthContext);
  const profilId = authContext?.user?.profil?.id;

  const dispatch = useAppDispatch();
  const serviceList = useAppSelector((state) => state?.service?.serviceData) || [];

  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (profilId) {
      setIsLoading(true);
      dispatch(getServiceByUserId(profilId)).then(() => setIsLoading(false));
    }
  }, [profilId]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = itemPerDashboradPrestation;

  // Filter services based on search term
  const filteredServices = Array.isArray(serviceList)
    ? serviceList.filter(
        (service: any) =>
          service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          service.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const paginatedServiceList = filteredServices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const length = filteredServices.length;
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const handleDeleteModalOpen = (service: Service) => {
    setSelectedService(service);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (selectedService && profilId) {
      await dispatch(deleteService(selectedService.id));
      setDeleteModalOpen(false);
      await dispatch(getServiceByUserId(profilId));
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className={`w-full p-6 ${className}`}>
      {/* Header Section */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center space-x-3 mb-4 sm:mb-0">
            <div className="w-12 h-12 bg-gradient-to-br from-eventToBee to-eventToBeePink rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mes Prestations</h1>
              <p className="text-gray-500 dark:text-gray-400">
                {length} prestation{length > 1 ? "s" : ""} disponible{length > 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <Link
            to="/add-service"
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-eventToBee to-eventToBeePink text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvelle prestation
          </Link>
        </div>

        {/* Search and View Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher une prestation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-eventToBee focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === "grid"
                  ? "bg-white dark:bg-gray-600 shadow-md text-eventToBee"
                  : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === "list"
                  ? "bg-white dark:bg-gray-600 shadow-md text-eventToBee"
                  : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <ListIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner color="blue" className="w-12 h-12 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Chargement de vos prestations...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Services Grid/List */}
          <AnimatePresence>
            {filteredServices.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-20"
              >
                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Package className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {searchTerm ? "Aucun résultat trouvé" : "Aucune prestation"}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {searchTerm
                    ? `Aucune prestation ne correspond à "${searchTerm}"`
                    : "Commencez par créer votre première prestation"}
                </p>
                {!searchTerm && (
                  <Link
                    to="/add-service"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-eventToBee to-eventToBeePink text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Créer ma première prestation
                  </Link>
                )}
              </motion.div>
            ) : (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className={`
                  ${viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}
                `}
              >
                {paginatedServiceList.map((service: any, index: number) => (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    whileHover={{ y: -5 }}
                    className={`
                      bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden
                      ${viewMode === "list" ? "flex items-center" : ""}
                    `}
                  >
                    {/* Service Image */}
                    <div
                      className={`${viewMode === "list" ? "w-24 h-24 flex-shrink-0" : "h-48"} relative overflow-hidden bg-gray-100 dark:bg-gray-700`}
                    >
                      {service?.images?.[0] ? (
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT + service.images[0]}`}
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                      )}

                      {/* Status Badge */}
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                          Actif
                        </span>
                      </div>
                    </div>

                    {/* Service Content */}
                    <div className={`p-6 ${viewMode === "list" ? "flex-1" : ""}`}>
                      <div className={`${viewMode === "list" ? "flex items-center justify-between" : ""}`}>
                        <div className={`${viewMode === "list" ? "flex-1" : ""}`}>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-1">
                            {service.title}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                            {service.description || "Aucune description disponible"}
                          </p>

                          {viewMode === "grid" && (
                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                              <MapPin className="w-4 h-4 mr-1" />
                              <span className="line-clamp-1">
                                {Array.isArray(service.region)
                                  ? service.region.join(", ")
                                  : service.region || "Non spécifié"}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className={`flex items-center space-x-2 ${viewMode === "list" ? "ml-4" : "mt-4"}`}>
                          <Link
                            to={`/prestations/${service.id}`}
                            className="p-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-lg transition-colors duration-200"
                            title="Voir"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/update-service/${service.id}`}
                            className="p-2 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40 text-green-600 dark:text-green-400 rounded-lg transition-colors duration-200"
                            title="Modifier"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDeleteModalOpen(service)}
                            className="p-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg transition-colors duration-200"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center mt-12"
            >
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </motion.div>
          )}

          {/* Delete Modal */}
          <DeleteModal
            open={deleteModalOpen}
            handleClose={() => setDeleteModalOpen(false)}
            handleDelete={handleDeleteConfirmed}
            category={{
              id: selectedService?.id || 0,
              designation: selectedService?.title || "",
            }}
          />
        </>
      )}
    </div>
  );
};

export default SectionGridFilterCardServiceAgent;
